import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import Money from '@/components/ui/Money'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import {
  failureMessage,
  importFailure,
  type PortfolioFailure,
} from '@/features/portfolio/api/errors'
import { useImportSave } from '@/features/portfolio/api/queries'
import { upgradeToast } from '@/features/subscriptions/lib/upgradeToast'
import { subscriptionRequiredFeature } from '@/features/subscriptions/lib/paywall'
import { formatQuantity } from '@/lib/format'
import { importRowDraft } from '@/schemas/portfolio'
import CheckTradesTable from '@/screens/portfolio/components/import/CheckTradesTable'
import ConfirmImportDialog from '@/screens/portfolio/components/import/ConfirmImportDialog'
import ImportResultPanel from '@/screens/portfolio/components/import/ImportResultPanel'
import ImportSteps from '@/screens/portfolio/components/import/ImportSteps'
import ImportTitleRow from '@/screens/portfolio/components/import/ImportTitleRow'
import ImportStrip from '@/screens/portfolio/components/import/ImportStrip'
import ImportSummaryCard from '@/screens/portfolio/components/import/ImportSummaryCard'
import ReviewHoldingsTable from '@/screens/portfolio/components/import/ReviewHoldingsTable'
import SkippedList from '@/screens/portfolio/components/import/SkippedList'
import UncoveredList from '@/screens/portfolio/components/import/UncoveredList'
import WaccOverlayTable from '@/screens/portfolio/components/import/WaccOverlayTable'
import { importSearchSchema } from '@/screens/portfolio/components/import/importSearch'
import {
  useImportSession,
  type ImportSession,
  type TradesReviewSession,
} from '@/screens/portfolio/components/import/importSession'
import {
  companyPicks,
  groupTrades,
  placeTrades,
  projectHoldings,
  reviewTrades,
  savedDraft,
  splitSkipped,
  totalAtCost,
} from '@/screens/portfolio/components/import/review'
import {
  CompanyNamesContext,
  useCompanyNames,
} from '@/screens/portfolio/components/import/useCompanyNames'
import { useWaccOverlay } from '@/screens/portfolio/components/import/useWaccOverlay'
import ImportStatementModal from '@/screens/portfolio/components/import-statement/ImportStatementModal'
import { portalNote } from '@/screens/portfolio/components/import-statement/portals'

export const Route = createFileRoute('/_app/portfolio/import')({
  validateSearch: importSearchSchema,
  component: ImportPortfolioScreen,
})

/**
 * Import portfolio — the review page (`5193:106113` Check trades,
 * `5261:52892` Review holdings, `5327:4599` Confirm import).
 *
 * Full width, as drawn (mock `#/portfolio/import`): no rail, the section strip
 * kept with Overview lit (nav-groups), the page's own title row under it
 * (`ImportTitleRow`), and the column capped at 1440 on a wider window. Every
 * Stock cell names its company (`useCompanyNames`, asks P11).
 *
 * ── What the page can promise ────────────────────────────────────────────────
 *
 * The trades are saved by posting the rows ON SCREEN back as JSON, so every
 * edit here — a kind, a kitta, a price, a removed row, a company picked for an
 * unknown symbol — is what `POST /import/` receives, and every figure is worked
 * out from those rows. The server re-screens and re-dedupes them, so a row the
 * book already holds is still skipped. A WACC report chosen with the trades is
 * a second request once they are saved; a WACC report on its own is a single
 * step that only rewrites average costs — and so is one whose trades turned
 * out to add nothing (the dialog's fallback, or "Apply only the WACC report").
 */

const IMPORT_SUBTITLE = 'Check what came out of your files before anything is saved.'

/** The Ask Mitra bar's chip: "Portfolio · Import portfolio", as the mock names the page. */
const PAGE_TITLE = 'Import portfolio'

/**
 * The review page's frame: no rail, the page's own title row, the column capped at 1440 (mock
 * `#/portfolio/import` at 1920), and the company names every Stock cell below reads.
 */
function ImportFrame({
  header,
  symbols,
  children,
}: {
  header: ReactNode
  symbols: readonly string[]
  children: ReactNode
}) {
  const names = useCompanyNames(symbols)
  return (
    <ScreenLayout fullWidth pageTitle={PAGE_TITLE} header={header}>
      <CompanyNamesContext value={names}>
        <div className="flex w-full max-w-360 flex-col gap-6">{children}</div>
      </CompanyNamesContext>
    </ScreenLayout>
  )
}

const count = (value: number, one: string, many: string): string =>
  `${formatQuantity(value)} ${value === 1 ? one : many}`

/**
 * What a refused save says in the confirm. `invalid` has no sentence of its own
 * (`failureMessage` leaves it to the fields), so the server's form-level line,
 * or the first row fault, stands in for a request with no cell to mark.
 */
const saveFailureMessage = (failure: PortfolioFailure): string => {
  if (failure.kind !== 'invalid') return failureMessage(failure)
  const rowFault = failure.rowErrors.flatMap((errors) => Object.values(errors))[0]
  return failure.formErrors[0] ?? rowFault ?? failureMessage(failure)
}

/**
 * The strip's primary action. Refused (`aria-disabled`, focus kept) with the
 * reason on screen and read with it, rather than a greyed button that says
 * nothing about why — and, where there is one, the way forward under it.
 */
function StripAction({
  label,
  blocked,
  instead = null,
  size = 'lg',
  className,
  onClick,
}: {
  label: string
  blocked: string | null
  /** What the member can do while the action is refused. Shown only then. */
  instead?: { label: string; onClick: () => void } | null
  /** `md` (36) for step 2's "Import 9 holdings" (mock 34), `lg` (48) elsewhere (mock 46). */
  size?: 'md' | 'lg'
  className: string
  onClick: () => void
}) {
  const reasonId = useId()
  return (
    <div className={className}>
      <Button
        type="button"
        size={size}
        className="w-full aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        aria-disabled={blocked !== null}
        aria-describedby={blocked === null ? undefined : reasonId}
        onClick={() => {
          if (blocked === null) onClick()
        }}
      >
        {label}
      </Button>
      {blocked === null ? null : (
        <>
          <p id={reasonId} className="mt-2 text-body-xs text-muted-foreground">
            {blocked}
          </p>
          {instead === null ? null : (
            <Button
              type="button"
              variant="secondary"
              className="mt-2 w-full hover:bg-popover"
              onClick={instead.onClick}
            >
              {instead.label}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

function ImportPortfolioScreen() {
  const { step } = Route.useSearch()
  const session = useImportSession((state) => state.session)
  const [picking, setPicking] = useState(false)

  if (session === null) {
    return (
      <ScreenLayout
        fullWidth
        pageTitle={PAGE_TITLE}
        header={<ImportTitleRow back="/portfolio" subtitle={IMPORT_SUBTITLE} />}
      >
        {/* A reload, or a link straight here: the files lived only in memory. */}
        <EmptyState
          kind="empty"
          size="page"
          title="Choose your files first"
          description="Files are not kept once this page reloads, so pick them again to check what is in them."
          action={
            <button
              type="button"
              className={stateActionClass('page')}
              onClick={() => {
                setPicking(true)
              }}
            >
              Choose files
            </button>
          }
        />
        <ImportStatementModal open={picking} onOpenChange={setPicking} />
      </ScreenLayout>
    )
  }

  // Keyed by the session: a new import is a new page, never the last one's
  // open confirm, send guard or row placement carried over.
  if (session.stage === 'done') return <ImportDone key={session.id} session={session} />
  if (session.mode === 'overlay') return <OverlayReview key={session.id} session={session} />
  return <TradesReview key={session.id} session={session} step={step} />
}

function ImportDone({ session }: { session: Extract<ImportSession, { stage: 'done' }> }) {
  const applyOverlay = useWaccOverlay(session.profile.scopeId)
  const retryOverlay = useImportSession((state) => state.retryOverlay)

  // The save answers on the page that sent it, often scrolled to its strip: the result starts at
  // the top, where its hero says what happened.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const holdings = session.mode === 'trades' ? session.imported.length : 0
  const symbols = useMemo(
    () => (session.mode === 'trades' ? session.imported.map((holding) => holding.symbol) : []),
    [session],
  )

  return (
    <ImportFrame
      symbols={symbols}
      header={
        <ImportTitleRow
          back="/portfolio"
          subtitle={
            holdings > 0
              ? holdings === 1
                ? `This holding is now in ${session.profile.name}.`
                : `These ${count(holdings, 'holding', 'holdings')} are now in ${session.profile.name}.`
              : `What was saved to ${session.profile.name}.`
          }
        />
      }
    >
      <ImportResultPanel
        session={session}
        onRetryOverlay={() => {
          // Only the report: the trades were saved before it was ever sent.
          const file = retryOverlay()
          if (file !== null) applyOverlay(session.id, file)
        }}
      />
    </ImportFrame>
  )
}

function TradesReview({
  session,
  step,
}: {
  session: TradesReviewSession
  step: 'trades' | 'holdings'
}) {
  const navigate = Route.useNavigate()
  const appNavigate = useNavigate()
  const editRow = useImportSession((state) => state.editRow)
  const removeRow = useImportSession((state) => state.removeRow)
  const pickCompany = useImportSession((state) => state.pickCompany)
  const refuseRows = useImportSession((state) => state.refuseRows)
  const finish = useImportSession((state) => state.finish)
  const switchToOverlay = useImportSession((state) => state.switchToOverlay)

  const { rows, preview } = session
  const review = useMemo(() => reviewTrades(rows), [rows])
  const holdings = useMemo(() => projectHoldings(rows, review), [rows, review])

  // Re-taken each time Check trades is entered — a refused save lands here too —
  // and never while a cell is typed into (see `Placement`).
  const [placement, setPlacement] = useState(() => placeTrades(review))
  const [placedOn, setPlacedOn] = useState(step)
  if (step !== placedOn) {
    setPlacedOn(step)
    if (step === 'trades') setPlacement(placeTrades(review))
  }
  const groups = useMemo(() => groupTrades(review, placement), [review, placement])
  const skipped = useMemo(() => splitSkipped(preview), [preview])
  const picks = useMemo(() => companyPicks(preview), [preview])
  const checks = holdings.map((holding) => holding.needsCheck)
  // Every symbol any table on this page draws: the rows, the server's skips, the picks.
  const symbols = useMemo(
    () => [
      ...rows.map((row) => row.draft.symbol),
      ...preview.skipped.map((row) => row.symbol),
      ...preview.needsDetail.map((entry) => entry.symbol),
    ],
    [rows, preview],
  )

  const trades = rows.length
  const found = preview.rows.length + preview.skipped.length
  const alreadyIn = skipped.alreadyIn.length
  const profileName = session.profile.name
  const { overlay } = session

  const importSave = useImportSave(session.profile.scopeId)
  const applyOverlay = useWaccOverlay(session.profile.scopeId)
  const [confirming, setConfirming] = useState(false)
  const [commitError, setCommitError] = useState<string>()

  /**
   * Set SYNCHRONOUSLY before the save. The server skips a trade the book
   * already holds, but not a cash dividend, and `isPending` only turns true on
   * the render after `mutate` — so two clicks inside one frame would both pass
   * it and send a dividend twice. Released only when a save FAILED, which
   * saved nothing.
   */
  const committed = useRef(false)

  const commit = () => {
    if (committed.current) return
    committed.current = true
    setCommitError(undefined)
    // Captured now: a refusal is aligned to the rows SENT, by position, and
    // the answer belongs to THIS session — by the time it lands the member may
    // have gone Back and started another, whose rows are keyed the same.
    const { id } = session
    const sent = rows
    // `mutateAsync`: the page unmounts as the session moves to `done`, and the
    // WACC report still has to go out after that.
    void importSave
      .mutateAsync({ portal: session.portal, rows: sent.map((row) => savedDraft(row.draft)) })
      .then(
        (result) => {
          setConfirming(false)
          if (finish(id, result) && overlay !== null) applyOverlay(id, overlay.file)
        },
        (error: unknown) => {
          committed.current = false
          const feature = subscriptionRequiredFeature(error)
          if (feature !== null) {
            // `import/` is gated on `portfolio.import` (CONTRACT §7).
            setConfirming(false)
            upgradeToast(feature, () => {
              void appNavigate({ to: '/subscription' })
            })
            return
          }
          const failure = importFailure(error) ?? { kind: 'failed' }
          // Rows the server named are marked where they can be fixed; a refusal
          // with no row to mark stays in the confirm, like any other failure.
          if (
            failure.kind === 'invalid' &&
            failure.rowErrors.some((errors) => Object.keys(errors).length > 0)
          ) {
            setConfirming(false)
            const marked = refuseRows(
              id,
              sent.map((row) => row.key),
              failure.rowErrors,
            )
            // Never pull the member back onto a page they have left.
            if (marked) void navigate({ search: { step: 'trades' } })
            return
          }
          setCommitError(saveFailureMessage(failure))
        },
      )
  }

  if (step === 'holdings') {
    const { total, unpriced } = totalAtCost(holdings)
    const blocked =
      trades === 0
        ? 'No trades are left to import.'
        : review.faulty > 0
          ? `Fix the ${count(review.faulty, 'marked row', 'marked rows')} in Check trades first.`
          : null
    return (
      <ImportFrame
        symbols={symbols}
        header={
          <ImportTitleRow
            back="/portfolio/import"
            subtitle={
              holdings.length > 0
                ? `These ${count(holdings.length, 'holding', 'holdings')} will be saved to ${profileName}.`
                : `These ${count(trades, 'trade', 'trades')} will be saved to ${profileName}.`
            }
            steps={<ImportSteps current="holdings" />}
          />
        }
      >
        <ImportSummaryCard
          title={`${count(holdings.length, 'holding', 'holdings')} from ${count(trades, 'trade', 'trades')}`}
          description="Check the kitta and average cost. Any trade can be corrected in Activity once it is saved."
          checks={checks}
          note={
            overlay === null
              ? null
              : `Your WACC report (${overlay.file.name}) sets the average cost of these holdings once the trades are saved.`
          }
        />

        {holdings.length === 0 ? (
          <EmptyState
            kind="empty"
            framed
            {...(trades === 0
              ? {
                  title: 'No trades to import',
                  description: 'Go back to Check trades to keep a row or pick a company.',
                }
              : {
                  title: 'These trades leave no holdings',
                  description:
                    'No company is left with units once these trades are saved. The trades themselves are still recorded.',
                })}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <ReviewHoldingsTable holdings={holdings} />
            <p className="text-body-xs text-muted-foreground-subtle">
              Worked out from these trades alone, with broker charges at equity rates. Anything
              already in {profileName} is added to these.
            </p>
          </div>
        )}

        <ImportStrip
          stats={[
            { label: 'Holdings', value: formatQuantity(holdings.length) },
            { label: 'Trades to add', value: formatQuantity(trades) },
            {
              label: 'Value at cost (Rs)',
              value: (
                <>
                  <Money value={total} whole />
                  {unpriced > 0 ? (
                    <span className="block text-body-xs font-normal text-muted-foreground">
                      {count(unpriced, 'holding', 'holdings')} with no price not counted
                    </span>
                  ) : null}
                </>
              ),
            },
          ]}
          action={
            <StripAction
              // The mock counts holdings; a run of dividends alone leaves none, so it counts trades.
              label={`Import ${holdings.length > 0 ? count(holdings.length, 'holding', 'holdings') : count(trades, 'trade', 'trades')}`}
              size="md"
              blocked={blocked}
              // Every trade gone, but the report can still set costs on what the
              // book already holds — and it was previewed against that book.
              instead={
                trades === 0 && overlay !== null && overlay.preview.rows.length > 0
                  ? { label: 'Apply only the WACC report', onClick: switchToOverlay }
                  : null
              }
              className="w-full lg:w-65"
              onClick={() => {
                setCommitError(undefined)
                setConfirming(true)
              }}
            />
          }
        />

        <ConfirmImportDialog
          kind="trades"
          open={confirming}
          onOpenChange={setConfirming}
          trades={trades}
          holdings={holdings.length}
          profileName={profileName}
          pending={importSave.isPending}
          error={commitError}
          onConfirm={commit}
        />
      </ImportFrame>
    )
  }

  const toCheck = groups.toCheck.length
  return (
    <ImportFrame
      symbols={symbols}
      header={
        <ImportTitleRow
          back="/portfolio"
          subtitle={IMPORT_SUBTITLE}
          steps={<ImportSteps current="trades" />}
        />
      }
    >
      <ImportSummaryCard
        title={`Found ${count(holdings.length, 'holding', 'holdings')} and ${count(trades, 'trade', 'trades')} in ${count(session.fileNames.length, 'file', 'files')}`}
        description={
          trades === 0
            ? 'No trades to import yet.'
            : toCheck === 0
              ? 'Everything is ready to import.'
              : `Check the ${count(toCheck, 'trade that needs', 'trades that need')} attention, then continue. Everything else is ready.`
        }
        checks={checks}
        note={portalNote(session.portal)}
      />

      {review.refused ? (
        <p
          role="alert"
          className="border border-destructive px-4 py-3 text-body-s text-destructive"
        >
          Some rows need fixing before they can be saved. They are marked below.
        </p>
      ) : null}

      <CheckTradesTable
        toCheck={groups.toCheck}
        ready={groups.ready}
        picks={picks}
        alreadyIn={skipped.alreadyIn}
        notImported={skipped.notImported}
        profileName={profileName}
        onEdit={editRow}
        onRemove={removeRow}
        onPick={pickCompany}
      />

      <ImportStrip
        stats={[
          { label: 'Holdings', value: formatQuantity(holdings.length) },
          { label: 'Trades found', value: formatQuantity(found) },
          ...(alreadyIn > 0
            ? [{ label: `Already in ${profileName}`, value: formatQuantity(alreadyIn) }]
            : []),
          { label: 'New trades to add', value: formatQuantity(trades) },
          { label: 'Trades to check', value: formatQuantity(toCheck) },
        ]}
        action={
          <Button
            type="button"
            size="lg"
            className="w-full lg:w-52"
            onClick={() => {
              void navigate({ search: { step: 'holdings' } })
            }}
          >
            Continue
          </Button>
        }
      />
    </ImportFrame>
  )
}

/**
 * A WACC report on its own: one step, no trades. Its preview rows are the
 * averages to set on scrips already held, and saving them as JSON under the
 * report's own portal applies the overlay — nothing on this page is edited,
 * because the report is the broker's figure and the only thing it can change.
 */
function OverlayReview({
  session,
}: {
  session: Extract<ImportSession, { stage: 'review'; mode: 'overlay' }>
}) {
  const finish = useImportSession((state) => state.finish)
  const appNavigate = useNavigate()
  const importSave = useImportSave(session.profile.scopeId)
  const [confirming, setConfirming] = useState(false)
  const [commitError, setCommitError] = useState<string>()
  // The trades page's guard. Sending the report twice sets the same averages
  // twice — harmless, but still a second write for one click.
  const committed = useRef(false)

  const { rows, uncovered, skipped } = session.preview
  const profileName = session.profile.name
  const symbols = useMemo(
    () => [...rows, ...uncovered, ...skipped].map((row) => row.symbol),
    [rows, uncovered, skipped],
  )

  const commit = () => {
    if (committed.current) return
    committed.current = true
    setCommitError(undefined)
    const { id } = session
    void importSave.mutateAsync({ portal: session.portal, rows: rows.map(importRowDraft) }).then(
      (result) => {
        setConfirming(false)
        finish(id, result)
      },
      (error: unknown) => {
        committed.current = false
        const feature = subscriptionRequiredFeature(error)
        if (feature !== null) {
          setConfirming(false)
          upgradeToast(feature, () => {
            void appNavigate({ to: '/subscription' })
          })
          return
        }
        setCommitError(saveFailureMessage(importFailure(error) ?? { kind: 'failed' }))
      },
    )
  }

  return (
    <ImportFrame
      symbols={symbols}
      header={
        <ImportTitleRow
          back="/portfolio"
          subtitle={`Set the average cost of holdings in ${profileName} from your WACC report.`}
        />
      }
    >
      <ImportSummaryCard
        title={`${count(rows.length, 'holding gets', 'holdings get')} a new average cost`}
        {...(session.tradesNote === null ? {} : { description: session.tradesNote })}
        checks={[]}
        note={portalNote(session.portal)}
      />

      {rows.length === 0 ? (
        <EmptyState
          kind="empty"
          framed
          title="Nothing to set"
          description={`The WACC report only updates holdings already in ${profileName}, and none of its companies are there.`}
        />
      ) : (
        <WaccOverlayTable rows={rows} />
      )}

      {uncovered.length > 0 || skipped.length > 0 ? (
        <div className="grid gap-4 bg-card px-4 py-4 sm:px-6 lg:grid-cols-2">
          <UncoveredList rows={uncovered} />
          <SkippedList rows={skipped} />
        </div>
      ) : null}

      <ImportStrip
        stats={[
          { label: 'Holdings to update', value: formatQuantity(rows.length) },
          { label: 'Not in this report', value: formatQuantity(uncovered.length) },
          { label: 'Skipped', value: formatQuantity(skipped.length) },
        ]}
        action={
          <StripAction
            label="Set average cost"
            blocked={rows.length === 0 ? `No holding in this report is in ${profileName}.` : null}
            className="w-full lg:w-65"
            onClick={() => {
              setCommitError(undefined)
              setConfirming(true)
            }}
          />
        }
      />

      <ConfirmImportDialog
        kind="overlay"
        open={confirming}
        onOpenChange={setConfirming}
        holdings={rows.length}
        profileName={profileName}
        pending={importSave.isPending}
        error={commitError}
        onConfirm={commit}
      />
    </ImportFrame>
  )
}
